/**
 * SegmentedControl: pick one of a few views or modes (JSON / Table, 1 day / 7 days / 30 days).
 *
 *   <SegmentedControl
 *     aria-label="Output view"
 *     value={view}
 *     onChange={setView}
 *     options={[{ value: "json", label: "JSON", icon: "mdi:code-json" }, { value: "table", label: "Table" }]}
 *   />
 *
 * Renders a radiogroup with a roving tab stop: Tab enters on the selected option, arrow keys move and select,
 * Home and End jump. size sm 28px | md 32px (default). fullWidth makes the options share the width equally.
 * An option can be `disabled`. aria-label (or aria-labelledby) is required.
 */
import { KeyboardEvent, ReactNode, useRef } from "react";
import { Icon } from "@iconify/react";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: string;
  disabled?: boolean;
  /** Accessible name when the label is an icon only. */
  ariaLabel?: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  size?: "sm" | "md";
  fullWidth?: boolean;
  className?: string;
}

export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  fullWidth = false,
  className = "",
  ...aria
}: SegmentedControlProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter(i => i >= 0);

  const move = (from: number, dir: 1 | -1 | "first" | "last") => {
    let next: number;
    if (dir === "first") next = enabled[0];
    else if (dir === "last") next = enabled[enabled.length - 1];
    else {
      const pos = enabled.indexOf(from);
      next = enabled[(pos + (dir as number) + enabled.length) % enabled.length];
    }
    if (next === undefined) return;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent, i: number) => {
    const dir =
      e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : e.key === "Home" ? "first" : e.key === "End" ? "last" : null;
    if (dir === null) return;
    e.preventDefault();
    move(i, dir);
  };

  const h = size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-13";

  return (
    <div
      role="radiogroup"
      {...aria}
      className={`${fullWidth ? "flex w-full" : "inline-flex"} items-center gap-0.5 rounded-control bg-gray-100 p-0.5 dark:bg-white/5 ${className}`}
    >
      {options.map((o, i) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            ref={el => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={o.ariaLabel}
            disabled={o.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={e => onKeyDown(e, i)}
            className={`${h} ${fullWidth ? "flex-1" : ""} relative before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors duration-150 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 ${
              selected
                ? "bg-surface text-fg shadow-sm dark:bg-white/15"
                : "text-fg-muted hover:text-fg"
            }`}
          >
            {o.icon && <Icon icon={o.icon} className="text-sm" aria-hidden="true" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
